/**
 * Utility functions for parsing and handling dates in various formats
 */

/**
 * Parse event dates in various formats including dd/mm/yyyy, mm/dd/yyyy, and text formats
 * @param dateString - The date string to parse
 * @returns Date object
 */
export const parseEventDate = (dateString: string): Date => {
  // Handle various date formats
  // "April 12, 2025", "March 15-16, 2025", "12/04/2025", "dd/mm/yyyy", etc.
  
  if (!dateString) {
    console.warn('Empty date string provided');
    return new Date();
  }
  
  // Handle date ranges - extract the first date
  // Patterns: "October 11-12, 2025", "April 24-26, 2025", "March 15-16, 2025"
  let cleanDate = dateString.trim();
  
  // Replace range pattern "11-12," with just the first day "11,"
  cleanDate = cleanDate.replace(/(\d{1,2})-\d{1,2}(,\s*\d{4})/, '$1$2');
  
  // For other hyphenated ranges, take the first part before the hyphen
  // But be careful not to split on hyphens in actual dates
  if (cleanDate.includes('-') && !cleanDate.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
    // If it looks like a range, take the first part
    if (cleanDate.match(/\w+\s+\d{1,2}-/)) {
      cleanDate = cleanDate.split('-')[0].trim();
    }
  }
  
  // Check for dd/mm/yyyy format first (European format)
  const ddmmyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const ddmmMatch = cleanDate.match(ddmmyyyyPattern);
  
  if (ddmmMatch) {
    const [, day, month, year] = ddmmMatch;
    const dayVal = parseInt(day);
    const monthVal = parseInt(month);
    
    // Validate reasonable ranges
    if (dayVal >= 1 && dayVal <= 31 && monthVal >= 1 && monthVal <= 12) {
      // Note: JavaScript Date constructor expects (year, month-1, day)
      const date = new Date(parseInt(year), monthVal - 1, dayVal);
      return date;
    }
  }
  
  // Check for mm/dd/yyyy format (American format)
  const mmddyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const mmddMatch = cleanDate.match(mmddyyyyPattern);
  
  if (mmddMatch) {
    const [, first, second, year] = mmddMatch;
    const firstVal = parseInt(first);
    const secondVal = parseInt(second);
    
    // If second value > 12, it's likely dd/mm/yyyy format
    if (secondVal > 12) {
      const date = new Date(parseInt(year), firstVal - 1, secondVal);
      return date;
    }
    // If first value > 12, it's likely mm/dd/yyyy but first is day
    if (firstVal > 12) {
      const date = new Date(parseInt(year), secondVal - 1, firstVal);
      return date;
    }
    // Otherwise, assume mm/dd/yyyy (American format)
    const date = new Date(parseInt(year), firstVal - 1, secondVal);
    return date;
  }
  
  // Create a more reliable date parser for text dates
  const monthNames: { [key: string]: number } = {
    'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
    'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11,
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
  };
  
  // Try to parse manually for "Month Day, Year" format
  // Handles: "October 11, 2025", "April 24, 2025", etc.
  const datePattern = /^(\w+)\s+(\d{1,2}),?\s+(\d{4})$/i;
  const match = cleanDate.match(datePattern);
  
  if (match) {
    const [, monthName, day, year] = match;
    const monthIndex = monthNames[monthName.toLowerCase()];
    
    if (monthIndex !== undefined) {
      const date = new Date(parseInt(year), monthIndex, parseInt(day));
      return date;
    }
  }
  
  // Try alternative pattern for dates without comma: "October 11 2025"
  const datePatternNoComa = /^(\w+)\s+(\d{1,2})\s+(\d{4})$/i;
  const matchNoComma = cleanDate.match(datePatternNoComa);
  
  if (matchNoComma) {
    const [, monthName, day, year] = matchNoComma;
    const monthIndex = monthNames[monthName.toLowerCase()];
    
    if (monthIndex !== undefined) {
      const date = new Date(parseInt(year), monthIndex, parseInt(day));
      return date;
    }
  }
  
  // Fallback to native Date parsing
  let parsed = new Date(cleanDate);
  
  // If that fails, try different approaches
  if (isNaN(parsed.getTime())) {
    // Try parsing with different separators
    const variations = [
      cleanDate,
      cleanDate.replace(',', ''),
      cleanDate.replace(/\s+/g, ' ').trim(),
      cleanDate.replace(/\./g, '/'), // Replace dots with slashes
      cleanDate.replace(/-/g, '/'), // Replace dashes with slashes
    ];
    
    for (const variation of variations) {
      parsed = new Date(variation);
      if (!isNaN(parsed.getTime())) {
        break;
      }
    }
  }
  
  // If parsing still fails, try to extract year and set a fallback date
  if (isNaN(parsed.getTime())) {
    console.warn(`Could not parse date: "${dateString}", using fallback`);
    const yearMatch = dateString.match(/\b(20\d{2})\b/);
    if (yearMatch) {
      const fallbackDate = new Date(parseInt(yearMatch[1]), 5, 7); // June 7 of that year
      return fallbackDate;
    }
    const currentFallback = new Date(2025, 5, 7); // June 7, 2025
    return currentFallback;
  }
  
  return parsed;
};

/**
 * Sort events by date (newest first - furthest in the future on top)
 * @param events - Array of events with date property
 * @returns Sorted array of events
 */
export const sortEventsByDateNewestFirst = <T extends { date: string }>(events: T[]): T[] => {
  // Create a new array to avoid mutating the original
  return [...events].sort((a, b) => {
    const dateA = parseEventDate(a.date);
    const dateB = parseEventDate(b.date);
    
    // If both dates are valid, sort by date (newest/furthest in future first)
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return dateB.getTime() - dateA.getTime();
    }
    
    // Fallback to string comparison (newest first)
    return b.date.localeCompare(a.date);
  });
};

/**
 * Sort events by date (oldest first)
 * @param events - Array of events with date property  
 * @returns Sorted array of events
 */
export const sortEventsByDateOldestFirst = <T extends { date: string }>(events: T[]): T[] => {
  // Create a new array to avoid mutating the original
  return [...events].sort((a, b) => {
    const dateA = parseEventDate(a.date);
    const dateB = parseEventDate(b.date);
    
    // If both dates are valid, sort by date (oldest first)
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return dateA.getTime() - dateB.getTime();
    }
    
    // Fallback to string comparison (oldest first)
    return a.date.localeCompare(b.date);
  });
};